import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { marked } from "marked";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import { Calendar, Tag, ArrowLeft, User } from "lucide-react";

marked.setOptions({ gfm: true, breaks: true });

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null); setNotFound(false);
    api.get(`/blog/posts/${slug}`)
      .then(r => setPost(r.data))
      .catch(() => setNotFound(true));
    api.get(`/blog/related/${slug}`).then(r => setRelated(r.data)).catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    // JSON-LD article structured data
    const ld = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.meta_description || post.excerpt,
      image: post.featured_image ? [post.featured_image] : [],
      datePublished: post.published_at,
      dateModified: post.updated_at || post.published_at,
      author: { "@type": "Organization", name: post.author || "Tech Alpha SSL" },
      publisher: {
        "@type": "Organization",
        name: "Tech Alpha SSL",
        logo: { "@type": "ImageObject", url: "https://techalphassl.com/favicon.svg" },
      },
      mainEntityOfPage: `https://techalphassl.com/blog/${post.slug}`,
    };
    let s = document.getElementById("blog-ld");
    if (!s) {
      s = document.createElement("script");
      s.type = "application/ld+json";
      s.id = "blog-ld";
      document.head.appendChild(s);
    }
    s.textContent = JSON.stringify(ld);

    const setMeta = (property, content, isName = false) => {
      const key = isName ? "name" : "property";
      let m = document.querySelector(`meta[${key}="${property}"]`);
      if (!m) { m = document.createElement("meta"); m.setAttribute(key, property); document.head.appendChild(m); }
      m.setAttribute("content", content || "");
    };
    setMeta("og:type", "article");
    setMeta("og:title", post.meta_title || post.title);
    setMeta("og:description", post.meta_description || post.excerpt || "");
    if (post.featured_image) setMeta("og:image", post.featured_image);
    setMeta("og:url", `https://techalphassl.com/blog/${post.slug}`);
    setMeta("twitter:card", "summary_large_image", true);
    setMeta("twitter:title", post.meta_title || post.title, true);
    setMeta("twitter:description", post.meta_description || post.excerpt || "", true);
    if (post.featured_image) setMeta("twitter:image", post.featured_image, true);

    return () => { if (s) s.textContent = ""; };
  }, [post]);

  if (notFound) return (
    <section className="pt-24 pb-24">
      <div className="container-page max-w-xl text-center">
        <h1 className="text-4xl font-semibold tracking-tighter">Post not found</h1>
        <Link to="/blog" className="btn-primary mt-6 inline-flex" data-testid="blog-notfound-back">Back to blog</Link>
      </div>
    </section>
  );

  if (!post) return <div className="pt-20 text-center text-sm text-muted-foreground">Loading…</div>;

  const html = marked.parse(post.content || "");

  return (
    <>
      <SEO
        title={post.meta_title || `${post.title} — Tech Alpha SSL`}
        description={post.meta_description || post.excerpt || ""}
        canonical={`https://techalphassl.com/blog/${post.slug}`}
      />
      <article className="pt-16 pb-16">
        <div className="container-page max-w-3xl">
          <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-[#1565FF]" data-testid="blog-back-link">
            <ArrowLeft className="h-3.5 w-3.5"/> Back to blog
          </Link>

          <div className="mt-6 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span className="rounded-full bg-[#1565FF]/10 text-[#1565FF] px-2 py-0.5">{post.category}</span>
            <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3"/> {new Date(post.published_at).toLocaleDateString()}</span>
            <span className="inline-flex items-center gap-1"><User className="h-3 w-3"/> {post.author}</span>
          </div>
          <h1 className="mt-4 text-4xl md:text-5xl font-semibold tracking-tighter leading-tight">{post.title}</h1>
          {post.excerpt && <p className="mt-4 text-lg text-muted-foreground leading-relaxed">{post.excerpt}</p>}
        </div>

        {post.featured_image && (
          <div className="container-page max-w-4xl mt-10">
            <img src={post.featured_image} alt={post.title} className="w-full rounded-2xl aspect-[16/8] object-cover" />
          </div>
        )}

        <div className="container-page max-w-3xl mt-12">
          <div
            className="prose prose-neutral dark:prose-invert max-w-none prose-headings:tracking-tight prose-a:text-[#1565FF] prose-strong:font-semibold"
            data-testid="blog-post-content"
            dangerouslySetInnerHTML={{ __html: html }}
          />

          {post.tags?.length > 0 && (
            <div className="mt-10 flex flex-wrap items-center gap-2">
              <Tag className="h-4 w-4 text-muted-foreground"/>
              {post.tags.map(t => (
                <Link key={t} to={`/blog?tag=${encodeURIComponent(t)}`} data-testid={`blog-tag-${t}`}
                  className="text-xs rounded-full bg-black/5 dark:bg-white/5 px-3 py-1 hover:bg-[#1565FF]/10 hover:text-[#1565FF]">
                  #{t}
                </Link>
              ))}
            </div>
          )}

          {related.length > 0 && (
            <div className="mt-16 pt-10 border-t border-black/5 dark:border-white/10">
              <h2 className="text-2xl font-semibold tracking-tight">Related posts</h2>
              <div className="mt-6 grid gap-4 md:grid-cols-3">
                {related.map(r => (
                  <Link key={r.id} to={`/blog/${r.slug}`} className="card-elevated p-5 hover:-translate-y-1 transition" data-testid={`blog-related-${r.slug}`}>
                    <div className="text-xs text-muted-foreground">{r.category}</div>
                    <div className="mt-2 text-sm font-semibold tracking-tight line-clamp-2">{r.title}</div>
                    {r.excerpt && <p className="mt-2 text-xs text-muted-foreground line-clamp-2">{r.excerpt}</p>}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </>
  );
}
